import React, { Component } from "react";
import { hotel as gnHotel } from "gn-shared";
import SectionTitle from "../../elements/sectionTitle";
import { CSSTransition } from "react-transition-group";
import EditRatePlanPanel from "./editRatePlanPanel";
import EditRoomPanel from "./editRoomPanel";
import {
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Checkbox,
  ThemeProvider,
  Box,
} from "@mui/material";
import { OpenInNew, Add, Delete, DragIndicator } from "@mui/icons-material";
import { State as ComponentState, MenuProps } from "./defs"; // Renamed imported State to avoid conflict
import { toast } from "react-toastify";
import { AuthService, YieldSheetService } from "../../services";
import "./roomStyle.css";
import withRouter from "../../withRouter";
import Popup from "../../../reusable-components/popup/popup";
import { connect } from "react-redux";
import { setActiveHotel } from "redux/Slice/hotelSlice";
import { YieldApi } from "api/yield";
import { baseTheme, tableTheme } from "mui-theme";
import { format } from "date-fns";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { IHotelConf } from "models/api.models";
import { RatePlan as RPInterface } from "./editRatePlanPanel";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import { ConfigApi } from "api/config";

// Define interfaces for API calls
interface User {
  username: string;
  permissions: {
    admin: string[];
  };
}

interface RoomType {
  id: string;
  shortDescription: string;
  minRate: string;
  maxRate: string;
  occupancy: string;
  childOccupancy: string;
  inventory: string;
  formula: string;
  sortOrder: number;
  deleted?: boolean;
  DOWVariation: {
    enabled: boolean;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
}

interface RatePlan extends RPInterface {
  searchModeOfSale: string;
  deleted?: boolean;
  sortOrder: number;
  defaultRate?: number;
}

interface Hotel {
  hotelId: string;
  info?: {
    level: string;
  };
  meta?: {
    hasRewards?: boolean;
  };
  getRoomTypes: () => RoomType[];
  getRatePlans: () => RatePlan[];
  ratePlans: { [key: string]: RatePlan };
  roomTypes: { [key: string]: RoomType };
}

interface Props {
  activeHotel: Hotel;
  user: User;
  router: {
    location: {
      pathname: string;
      search: string;
      state: ComponentState; // Use renamed imported State
      hash: string;
    };
  };
  setActiveHotel: (hotel: Hotel) => void;
}

// Define component state interface extending the imported one if necessary, or use a new one
interface State extends ComponentState {
  ratePlanArray: RatePlan[];
  roomTypeData: RoomType[];
  unEditable: boolean;
  unEditableRP: boolean;
  isOverflow: boolean[];
  isOverflowRT: boolean[];
  counter: number;
  searchInput: string;
  searchDropDown: string[];
  isDraging: boolean;
  rtUpdatedStatus: boolean;
  rpUpdatedStatus: boolean;
  previousLocation: string;
  key: number;
  setRateFilter: string;
  searchModeOfSale: string;
}

const emptyRp: RatePlan = {
  modeOfSale: "",
  id: "",
  longDescription: "",
  shortDescription: "",
  searchModeOfSale: "",
  isManual: false,
  applies: [],
  sortOrder: 0,
  DOWVariation: {
    enabled: false,
    setRate: {},
  },
};

const emptyRT: RoomType = {
  shortDescription: "",
  id: "",
  minRate: "",
  maxRate: "",
  occupancy: "",
  childOccupancy: "",
  inventory: "",
  formula: "",
  sortOrder: 0,
  DOWVariation: {
    enabled: false,
    monday: "",
    tuesday: "",
    wednesday: "",
    thursday: "",
    friday: "",
    saturday: "",
    sunday: "",
  },
};

class RoomConfig extends Component<Props, State> {
  readonly state: State = {
    roomData: JSON.parse(JSON.stringify(emptyRT)),
    isRTPanelOpened: false,
    isRTPanelNew: true,
    rateplanData: JSON.parse(JSON.stringify(emptyRp)),
    isRPPanelOpened: false,
    isRPPanelNew: false,
    isDeleteRpDialogOpened: false,
    isDeleteRtDialogOpened: false,
    rpToDelete: {} as RatePlan,
    rtToDelete: {} as RoomType,
    unEditable: false,
    unEditableRP: false,
    isOverflow: [],
    isOverflowRT: [],
    ratePlanArray: [],
    roomTypeData: [],
    counter: 0,
    searchInput: "",
    searchDropDown: [],
    isDraging: true,
    rtUpdatedStatus: true,
    rpUpdatedStatus: true,
    previousLocation: "",
    key: 0,
    setRateFilter: "All",
    searchModeOfSale: "All",
  };
  private isAdmin: boolean;
  private hotel: Hotel | null = null;

  constructor(props: Props) {
    super(props);
    this.isAdmin =
      hasPermission(ACTIONS.ADD_ROOM) ||
      (this.props.activeHotel.info?.level == "Pro" &&
        hasPermission(ACTIONS.MANAGE_PRO_HOTEL));
  }

  initializeHotelData() {
    if (!this.hotel) return;

    const rtArray = this.hotel
      .getRoomTypes()
      .sort((a: { sortOrder: number }, b: { sortOrder: number }) => {
        return a.sortOrder - b.sortOrder;
      });

    const rtModArray: RoomType[] = [];
    rtArray.forEach((rt: RoomType, index: number) => {
      if (typeof rt.deleted == "undefined") {
        rtModArray.push(rtArray[index]);
      }
    });

    const rpsShortDescription = this.hotel
      .getRatePlans()
      .sort((a: { sortOrder: number }, b: { sortOrder: number }) => {
        return a.sortOrder - b.sortOrder;
      });

    const rpsModShortDescription: RatePlan[] = [];
    rpsShortDescription.forEach((rp: RatePlan, index: number) => {
      if (typeof rp.deleted == "undefined") {
        rpsModShortDescription.push(rpsShortDescription[index]);
      }
    });

    // Create new refs arrays
    const rts = rtModArray.map(
      (rt: { shortDescription: string }) => rt.shortDescription,
    );
    const rps = rpsModShortDescription.map(
      (data: { shortDescription: string }) => data.shortDescription,
    );

    // Update state with new data
    this.setState({
      roomTypeData: rtModArray,
      ratePlanArray: rpsModShortDescription,
      isOverflow: rps.map(() => false),
      isOverflowRT: rts.map(() => false),
    });
  }

  async componentDidMount() {
    await AuthService.revanista.user.identify();

    if (
      this.props.activeHotel &&
      Object.keys(this.props.activeHotel).length > 0
    ) {
      const hotelObject = JSON.parse(JSON.stringify(this.props.activeHotel));
      this.hotel = gnHotel(hotelObject);
      this.initializeHotelData();
    }
  }

  async componentDidUpdate(prevProps: { activeHotel: Hotel }) {
    // Check if activeHotel has changed and is not empty
    if (
      prevProps.activeHotel !== this.props.activeHotel &&
      this.props.activeHotel &&
      Object.keys(this.props.activeHotel).length > 0
    ) {
      const hotelObject = JSON.parse(JSON.stringify(this.props.activeHotel));
      this.hotel = gnHotel(hotelObject);
      this.initializeHotelData();
    }
  }

  onHotelUpdate = async () => {
    if (!this.props.activeHotel?.hotelId) return;

    const response = await ConfigApi.getHotelConfig(
      Number(this.props.activeHotel.hotelId),
    );
    if (response) {
      const hotelObject = gnHotel(response);
      this.props.setActiveHotel(hotelObject);
    }
  };

  closeRPPanel = () => {
    this.setState({ isRPPanelOpened: false });
  };
  closeRoomPanel = () => {
    this.setState({ isRTPanelOpened: false });
  };

  updateRPinStore = () => {
    this.onHotelUpdate();
  };

  updateRoomTypeinStore = () => {
    this.onHotelUpdate();
  };

  deleteRateplan = async (rp: { id: string; shortDescription: string }) => {
    const hotel = this.hotel;
    if (!hotel) return;

    this.setState({ isDeleteRpDialogOpened: false });

    const toastDeleteRP = toast.promise(
      YieldSheetService.revanista.hotel.deleteRateplan(
        rp.id,
        Number(hotel.hotelId),
      ),
      {
        pending:
          "Deleting RatePlan - '" + rp.shortDescription + "' in progress...",
        error: "Error deleting Rate Plan",
        success: "Rate Plan deleted",
      },
    );

    toastDeleteRP.then(() => {
      if (hotel.ratePlans) {
        delete hotel.ratePlans[rp.id];
        this.onHotelUpdate();
        // No need to set state hotel here, onHotelUpdate handles it via Redux
      }
    });
  };

  deleteRoomType = async (rt: { id: string; shortDescription: string }) => {
    const hotel = this.hotel;
    if (!hotel) return;

    this.setState({ isDeleteRtDialogOpened: false });

    const deleteRTandUpdate = async () => {
      await YieldSheetService.revanista.hotel.deleteRoomType(
        rt.id,
        Number(hotel.hotelId),
      );
      // Fetch updated config after deletion
      await this.onHotelUpdate();
      return rt;
    };

    toast.promise(deleteRTandUpdate(), {
      pending:
        "Deleting Room Type - '" + rt.shortDescription + "' in progress...",
      error: "Error deleting Room Type",
      success: "Room Type deleted",
    });
  };

  openDeleteDialog = (rp: { id: string; shortDescription: string }) => {
    this.setState({
      rpToDelete: JSON.parse(JSON.stringify(rp)),
      isDeleteRpDialogOpened: true,
    });
  };

  closeDeleteDialog = () => {
    this.setState({
      isDeleteRpDialogOpened: false,
    });
  };

  openDeleteRtDialog = (rt: { id: string; shortDescription: string }) => {
    this.setState({
      rtToDelete: JSON.parse(JSON.stringify(rt)),
      isDeleteRtDialogOpened: true,
    });
  };

  closeDeleteRtDialog = () => {
    this.setState({
      isDeleteRtDialogOpened: false,
    });
  };

  openEditRPPanel = (rpId: string | null) => {
    let rp;
    let isNew = false;
    if (!rpId || !this.hotel) {
      rp = JSON.parse(JSON.stringify(emptyRp));
      isNew = true;
    } else {
      rp = JSON.parse(JSON.stringify(this.hotel.ratePlans[rpId]));
    }
    this.setState({ unEditableRP: true });
    this.setState({
      rateplanData: rp,
      isRPPanelOpened: true,
      isRPPanelNew: isNew,
    });
  };

  openEditRPPanelByPlus = (rpId: string | null) => {
    let rp;
    let isNew = false;
    if (!rpId || !this.hotel) {
      rp = JSON.parse(JSON.stringify(emptyRp));
      isNew = true;
    } else if (this.hotel?.ratePlans) {
      // Check if hotel and ratePlans exist
      rp = JSON.parse(JSON.stringify(this.hotel.ratePlans[rpId]));
    } else {
      rp = JSON.parse(JSON.stringify(emptyRp)); // Fallback if ratePlans don't exist
      isNew = true;
    }
    this.setState({ unEditableRP: false });
    this.setState({
      rateplanData: rp,
      isRPPanelOpened: true,
      isRPPanelNew: isNew,
    });
  };

  openEditRoomPanel = (rtID: string | null) => {
    let rt;
    let isNew = false;
    if (!rtID || !this.hotel) {
      rt = JSON.parse(JSON.stringify(emptyRT));
      isNew = true;
    } else {
      rt = JSON.parse(JSON.stringify(this.hotel.roomTypes[rtID]));
    }
    this.setState({ unEditable: true });
    this.setState({
      roomData: rt,
      isRTPanelOpened: true,
      isRTPanelNew: isNew,
    });
  };

  openEditRoomPanelByPlus = (rtID: string | null) => {
    let rt;
    let isNew = false;
    if (!rtID || !this.hotel) {
      rt = JSON.parse(JSON.stringify(emptyRT));
      isNew = true;
    } else if (this.hotel?.roomTypes) {
      // Check if hotel and roomTypes exist
      rt = JSON.parse(JSON.stringify(this.hotel.roomTypes[rtID]));
    } else {
      rt = JSON.parse(JSON.stringify(emptyRT)); // Fallback if roomTypes don't exist
      isNew = true;
    }
    this.setState({ unEditable: false });
    this.setState({
      roomData: rt,
      isRTPanelOpened: true,
      isRTPanelNew: isNew,
    });
  };

  getRoomTypeLabels = (applies?: string[]) => {
    if (!applies) return "";

    const rtDetail = this.state.roomTypeData.map(
      (data: { id: string; shortDescription: string }) => {
        return { label: data.shortDescription, value: data.id };
      },
    );

    return applies
      .map((rtId) => {
        const found = rtDetail.find(
          (rt: { label: string; value: string }) => rt.value === rtId,
        );
        return found ? found.label : null;
      })
      .filter((label) => label !== null)
      .join(", ");
  };

  handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const name = event.target.name;
    const value = event.target.value;

    const rtDetail = this.state.roomTypeData.map(
      (data: { id: string; shortDescription: string }) => {
        return { label: data.shortDescription, value: data.id };
      },
    );

    switch (name) {
      case "search":
        console.log("value", value);
        this.setState({
          searchInput: value as string, // Cast to string
        });
        break;

      case "roomType": {
        const { searchDropDown } = this.state;

        if ((value as string[])[(value as string[]).length - 1] === "all") {
          const options = rtDetail.map(
            (data: { label: string; value: string }) => data.value,
          );

          this.setState({
            searchDropDown:
              this.state.roomTypeData.length === searchDropDown.length
                ? []
                : options,
          });

          return;
        }
        this.setState({
          searchDropDown: [...(value as string[])],
        });
        break;
      }
      case "searchModeOfSale":
        this.setState({
          searchModeOfSale: value as string,
        });
        break;

      case "setRateFilter":
        this.setState({
          setRateFilter: value as string,
        });
        break;

      default:
        break;
    }
  };

  onDragEndRoomTypes = async (result: DropResult) => {
    if (!result.destination || !this.isAdmin || !this.hotel) return;

    const currentRoomTypeData = Array.from(this.state.roomTypeData);
    const [reorderedItem] = currentRoomTypeData.splice(result.source.index, 1);
    currentRoomTypeData.splice(result.destination.index, 0, reorderedItem);

    // Update local state first for immediate UI feedback
    this.setState({ roomTypeData: currentRoomTypeData });

    // Prepare the updated hotel config
    const updatedHotelConfig = JSON.parse(JSON.stringify(this.hotel)); // Deep copy
    const updatedRoomTypes: { [key: string]: RoomType } = {};

    currentRoomTypeData.forEach((roomType: RoomType, index: number) => {
      // Type is now consistent with array type
      const rtId = roomType.id;
      if (updatedHotelConfig.roomTypes[rtId]) {
        updatedHotelConfig.roomTypes[rtId].sortOrder = index;
        updatedRoomTypes[rtId] = updatedHotelConfig.roomTypes[rtId];
      }
    });

    // Ensure all original room types are included, even if not in currentRoomTypeData (shouldn't happen with current logic)
    Object.keys(this.hotel.roomTypes).forEach((rtId) => {
      if (!updatedRoomTypes[rtId]) {
        // If an original RT is somehow missing (e.g., deleted but still in hotel object?), add it back.
        // This might need adjustment based on how deleted RTs are handled.
        updatedRoomTypes[rtId] = this.hotel!.roomTypes[rtId];
      }
    });

    updatedHotelConfig.roomTypes = updatedRoomTypes;

    const payload: IHotelConf = {
      hotel: updatedHotelConfig,
      start: format(new Date(), "yyyy-MM-dd"),
    };

    try {
      await toast.promise(ConfigApi.updateHotelConfig(payload), {
        pending: "Updating room type order...",
        success: "Room type order updated successfully.",
        error: "Failed to update room type order.",
      });
      // Re-fetch hotel config to ensure consistency after update
      await this.onHotelUpdate();
    } catch (error) {
      console.error("Error saving room type sort order:", error);
      // Optionally revert state if API call fails
      // this.setState({ roomTypeData: Array.from(this.state.roomTypeData) }); // Revert if needed
    }
  };

  onDragEndRatePlans = async (result: DropResult) => {
    if (!result.destination || !this.isAdmin || !this.hotel) return;

    // Reorder the *entire* ratePlanArray from state
    const currentRatePlanData = Array.from(this.state.ratePlanArray);
    const [reorderedItem] = currentRatePlanData.splice(result.source.index, 1);
    currentRatePlanData.splice(result.destination.index, 0, reorderedItem);

    // Update local state first for immediate UI feedback
    this.setState({ ratePlanArray: currentRatePlanData });

    // Prepare the updated hotel config
    const updatedHotelConfig = JSON.parse(JSON.stringify(this.hotel)); // Deep copy
    const updatedRatePlans: { [key: string]: RatePlan } = {};

    currentRatePlanData.forEach((ratePlan: RatePlan, index: number) => {
      const rpId = ratePlan.id;
      if (updatedHotelConfig.ratePlans[rpId]) {
        updatedHotelConfig.ratePlans[rpId].sortOrder = index;
        updatedRatePlans[rpId] = updatedHotelConfig.ratePlans[rpId];
      }
    });

    // Ensure all original rate plans are included
    Object.keys(this.hotel.ratePlans).forEach((rpId) => {
      if (!updatedRatePlans[rpId]) {
        // If an original RP is somehow missing, add it back.
        updatedRatePlans[rpId] = this.hotel!.ratePlans[rpId];
      }
    });

    updatedHotelConfig.ratePlans = updatedRatePlans;

    const payload: IHotelConf = {
      hotel: updatedHotelConfig,
      start: format(new Date(), "yyyy-MM-dd"),
    };

    try {
      // Use updateHotelConfig instead of writeRpSortOrder
      await toast.promise(ConfigApi.updateHotelConfig(payload), {
        pending: "Updating rate plan order...",
        success: "Rate plan order updated successfully.",
        error: "Failed to update rate plan order.",
      });
      // Re-fetch hotel config to ensure consistency
      await this.onHotelUpdate();
    } catch (error) {
      console.error("Error saving rate plan sort order:", error);
      // Optionally revert state if API call fails
      // this.setState({ ratePlanArray: Array.from(this.state.ratePlanArray) }); // Revert if needed
    }
  };

  render() {
    const { searchInput, searchDropDown, searchModeOfSale, setRateFilter } =
      this.state;
    const revenuePerm = hasPermission(ACTIONS.EDIT_ROOM);
    const rtDetail = this.state.roomTypeData.map(
      (data: { id: string; shortDescription: string }) => {
        return { label: data.shortDescription, value: data.id };
      },
    );

    // Filter the data *after* getting it from state for display purposes
    // The drag-and-drop operates on the full state.ratePlanArray
    const filteredData = this.state.ratePlanArray
      .filter((item: RatePlan) => {
        // Type is now consistent with array type
        const matchesSearchInput =
          searchInput.length === 0 ||
          item.shortDescription
            .toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          item.id.toLowerCase().includes(searchInput.toLowerCase());

        const matchesModeOfSale =
          (searchModeOfSale === "All" && item) ||
          item.modeOfSale === searchModeOfSale;

        const matchSetRate =
          (setRateFilter === "All" && item) ||
          (setRateFilter === "set rate" &&
            item.defaultRate &&
            item.defaultRate !== 0) || // Check if defaultRate exists
          (setRateFilter === "without set rate" &&
            item.formula &&
            item.formula.length !== 0);

        const matchesSearchDropDown =
          (searchDropDown.length === 0 && item) ||
          (searchDropDown.includes("all") && item) ||
          searchDropDown.every((element: string) =>
            item.applies.includes(element),
          );

        return (
          matchesSearchInput &&
          matchesModeOfSale &&
          matchSetRate &&
          matchesSearchDropDown
        );
      })
      .sort((a: { sortOrder: number }, b: { sortOrder: number }) => {
        // Keep sorting for display based on potentially updated sortOrder
        return a.sortOrder - b.sortOrder;
      });

    return (
      <ThemeProvider theme={baseTheme}>
        <div className="content">
          {!revenuePerm && (
            <p
              style={{
                color: "red",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              This page is admin locked.{" "}
              {this.props.activeHotel.info?.level == "Pro"
                ? "Please contact your admin should you need something changed"
                : "Please contact us should you need something changed"}
              .
            </p>
          )}
          <SectionTitle {...{ value: "Room Types" }} />
          {this.isAdmin && (
            <>
              <br />
              {!this.state.isRTPanelOpened && (
                <Button
                  aria-label="split"
                  variant="contained"
                  color="info"
                  component="span"
                  id="add-room-type"
                  onClick={() => {
                    this.openEditRoomPanelByPlus(null);
                  }}
                  style={{
                    width: "50px",
                    height: "100%",
                    marginBottom: "10px",
                  }}
                >
                  <Add />
                </Button>
              )}
            </>
          )}
          <br />
          <CSSTransition
            in={this.state.isRTPanelOpened}
            appear={true}
            exit={true}
            timeout={1000}
            classNames="fade"
            unmountOnExit
          >
            <EditRoomPanel
              key={this.state.roomData.id || "new-rt"} // Add key for new panel
              user={this.props.user}
              hotel={this.hotel}
              roomType={this.state.roomData}
              allData={this.state.roomTypeData}
              onclose={this.closeRoomPanel}
              isNew={this.state.isRTPanelNew}
              onUpdate={this.updateRoomTypeinStore}
              unEditable={this.state.unEditable}
              counter={this.state.counter}
            ></EditRoomPanel>
          </CSSTransition>
          <Container>
            <ThemeProvider theme={tableTheme}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {this.isAdmin && <TableCell width="50px"></TableCell>}
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">Min Rate</TableCell>
                      <TableCell align="center">Max Rate</TableCell>
                      <TableCell align="center">Occupancy</TableCell>
                      <TableCell align="center">Child Occ.</TableCell>
                      <TableCell align="center">Inventory</TableCell>
                      <TableCell align="center">Formula</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <DragDropContext onDragEnd={this.onDragEndRoomTypes}>
                    <Droppable droppableId="roomTypesTable">
                      {(provided) => (
                        <TableBody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {this.state.roomTypeData // Already sorted by sortOrder in state/initializeHotelData
                            .map((rt: RoomType, index: number) => (
                              <Draggable
                                key={rt.id}
                                draggableId={rt.id}
                                index={index}
                                isDragDisabled={!this.isAdmin}
                              >
                                {(provided, snapshot) => (
                                  <TableRow
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      background: snapshot.isDragging
                                        ? "#f5f5f5"
                                        : "inherit",
                                    }}
                                  >
                                    {this.isAdmin && (
                                      <TableCell {...provided.dragHandleProps}>
                                        <DragIndicator />
                                      </TableCell>
                                    )}
                                    <TableCell align="center">
                                      {rt.shortDescription}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.id}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.minRate}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.maxRate}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.occupancy}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.childOccupancy || "0"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {rt.inventory || "0"}
                                    </TableCell>
                                    <TableCell>
                                      {rt.DOWVariation.enabled
                                        ? "Day of week variation"
                                        : rt.formula}
                                    </TableCell>
                                    <TableCell
                                      style={{ width: "160px" }}
                                      align="center"
                                    >
                                      <Tooltip title="Open">
                                        <IconButton
                                          id={`open_${rt.id}`}
                                          color="primary"
                                          onClick={(
                                            event: React.MouseEvent<HTMLButtonElement>,
                                          ) => {
                                            this.openEditRoomPanel(rt.id);
                                            event.stopPropagation();
                                          }}
                                        >
                                          <OpenInNew />
                                        </IconButton>
                                      </Tooltip>
                                      {this.isAdmin && (
                                        <Tooltip title="Remove">
                                          <IconButton
                                            id={`delete_${rt.id}`}
                                            color="primary"
                                            onClick={() => {
                                              this.openDeleteRtDialog({
                                                id: rt.id,
                                                shortDescription:
                                                  rt.shortDescription,
                                              });
                                            }}
                                          >
                                            <Delete />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Draggable>
                            ))}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </TableContainer>
            </ThemeProvider>
          </Container>

          <br />
          <Divider></Divider>
          <SectionTitle {...{ value: "Rate Plans" }} />
          {this.isAdmin && (
            <>
              <br />
              {!this.state.isRPPanelOpened && (
                <Button
                  aria-label="split"
                  variant="contained"
                  color="info"
                  id="add-rate-plan"
                  component="span"
                  onClick={() => {
                    this.openEditRPPanelByPlus(null);
                  }}
                  style={{
                    width: "50px",
                    height: "100%",
                    marginBottom: "10px",
                  }}
                >
                  <Add />
                </Button>
              )}
            </>
          )}
          <br />
          <CSSTransition
            in={this.state.isRPPanelOpened}
            appear={true}
            exit={true}
            timeout={1000}
            classNames="fade"
            unmountOnExit
          >
            <EditRatePlanPanel
              key={this.state.rateplanData.id || "new-rp"} // Add key for new panel
              user={this.props.user}
              hotel={this.hotel}
              ratePlan={this.state.rateplanData}
              onclose={this.closeRPPanel}
              isNew={this.state.isRPPanelNew}
              onUpdate={this.updateRPinStore}
              unEditableRP={this.state.unEditableRP}
              counter={this.state.counter}
            ></EditRatePlanPanel>
          </CSSTransition>
          <Container>
            <ThemeProvider theme={tableTheme}>
              <br />
              <div style={{ width: "100%" }}>
                <Box sx={{ "& .MuiTextField-root": { m: 1, width: "25ch" } }}>
                  <div>
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      label="Search"
                      variant="outlined"
                      name="search"
                      onChange={(e) =>
                        this.handleChange(
                          e as SelectChangeEvent<string | string[]>,
                        )
                      }
                    />
                    <FormControl sx={{ m: 1, width: 250 }}>
                      <InputLabel>Room Type</InputLabel>
                      <Select
                        label="Room Type"
                        name="roomType"
                        multiple
                        value={this.state.searchDropDown}
                        onChange={(e) => this.handleChange(e)}
                        renderValue={() => (
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            {(this.state.searchDropDown as string[])
                              .filter((value: string) => value !== "all")
                              .map((value: string) => {
                                const chip = rtDetail.find(
                                  (item: { label: string; value: string }) =>
                                    item.value === value,
                                );
                                const label =
                                  typeof chip != "undefined" ? chip.label : "";

                                if (label) {
                                  return (
                                    <Chip
                                      key={value}
                                      label={label}
                                      style={{ margin: "2px" }}
                                    />
                                  );
                                }
                                return null;
                              })}
                          </div>
                        )}
                        MenuProps={MenuProps}
                      >
                        {rtDetail.map(
                          (rt: { label: string; value: string }) => (
                            <MenuItem key={rt.value} value={rt.value}>
                              <Checkbox
                                checked={
                                  this.state.searchDropDown.indexOf(rt.value) >
                                  -1
                                }
                              />

                              {rt.label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, width: 250 }}>
                      <InputLabel>Mode of Sale</InputLabel>
                      <Select
                        value={this.state.searchModeOfSale}
                        fullWidth
                        label="Mode of Sale"
                        variant="outlined"
                        name="searchModeOfSale"
                        onChange={(e) => this.handleChange(e)}
                      >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="OTA">OTA</MenuItem>
                        <MenuItem value="OWS">OWS</MenuItem>
                        <MenuItem value="GDS">GDS</MenuItem>
                        {this.hotel?.meta?.hasRewards && (
                          <MenuItem value="Rewards">Rewards</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, width: 250 }}>
                      <InputLabel>Set Rate</InputLabel>
                      <Select
                        fullWidth
                        label="Set Rate"
                        value={this.state.setRateFilter}
                        variant="outlined"
                        name="setRateFilter"
                        onChange={(e) => this.handleChange(e)}
                      >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="set rate">Enabled</MenuItem>
                        <MenuItem value="without set rate">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </Box>
              </div>
              <br />
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {this.isAdmin && <TableCell width="50px"></TableCell>}
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">Mode of Sale</TableCell>
                      <TableCell align="center">Room Type</TableCell>
                      <TableCell align="center">Formula</TableCell>
                      <TableCell align="center">Temporary</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <DragDropContext onDragEnd={this.onDragEndRatePlans}>
                    <Droppable droppableId="ratePlansTable">
                      {(provided) => (
                        <TableBody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {/* Render using filteredData for display */}
                          {filteredData.map(
                            (
                              rp: RatePlan, // Use RatePlan interface
                            ) => {
                              // Find the original index in the state array for the Draggable index prop
                              const originalIndex =
                                this.state.ratePlanArray.findIndex(
                                  (item) => item.id === rp.id,
                                );
                              return (
                                <Draggable
                                  key={rp.id}
                                  draggableId={rp.id}
                                  // Use the originalIndex for react-beautiful-dnd
                                  index={originalIndex}
                                  isDragDisabled={!this.isAdmin}
                                >
                                  {(provided, snapshot) => (
                                    <TableRow
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        background: snapshot.isDragging
                                          ? "#f5f5f5"
                                          : "inherit",
                                      }}
                                    >
                                      {this.isAdmin && (
                                        <TableCell
                                          align="center"
                                          {...provided.dragHandleProps}
                                        >
                                          <DragIndicator />
                                        </TableCell>
                                      )}
                                      <TableCell align="center">
                                        {rp.id}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="description-cell"
                                      >
                                        {rp.shortDescription}
                                      </TableCell>
                                      <TableCell align="center">
                                        {rp.modeOfSale}
                                      </TableCell>
                                      <TableCell align="center">
                                        <div
                                          style={{
                                            maxWidth: "200px",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                          }}
                                        >
                                          {this.getRoomTypeLabels(rp.applies) ||
                                            "N/A"}
                                        </div>
                                      </TableCell>
                                      <TableCell align="center">
                                        <div
                                          style={{
                                            maxWidth: "200px",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                          }}
                                        >
                                          {rp.DOWVariation.enabled
                                            ? "Day of week variation"
                                            : rp.isManual
                                              ? (rp.defaultRate
                                                  ?.toString()
                                                  .replace(/\+/g, " + ") ??
                                                "N/A")
                                              : rp.formula
                                                  ?.toString()
                                                  .replace(/\+/g, " + ") ||
                                                "N/A"}
                                        </div>
                                      </TableCell>
                                      <TableCell align="center">
                                        {(() => {
                                          const ac: any = (rp as any)
                                            .autoCloseOut;
                                          if (Array.isArray(ac)) {
                                            if (ac.length === 0) return "-";
                                            return (
                                              <Tooltip
                                                title={
                                                  <div>
                                                    {ac.map(
                                                      (p: any, idx: number) => (
                                                        <div key={idx}>
                                                          {format(
                                                            new Date(
                                                              p.openStart,
                                                            ),
                                                            "dd MMM yyyy",
                                                          )}{" "}
                                                          -{" "}
                                                          {format(
                                                            new Date(p.openEnd),
                                                            "dd MMM yyyy",
                                                          )}
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                }
                                              >
                                                <span>
                                                  <Chip
                                                    size="small"
                                                    label={`${ac.length} period${ac.length > 1 ? "s" : ""}`}
                                                  />
                                                </span>
                                              </Tooltip>
                                            );
                                          }
                                          if (
                                            ac &&
                                            ac.openStart &&
                                            ac.openEnd
                                          ) {
                                            return (
                                              <Tooltip
                                                title={`${format(new Date(ac.openStart), "dd MMM yyyy")} - ${format(
                                                  new Date(ac.openEnd),
                                                  "dd MMM yyyy",
                                                )}`}
                                              >
                                                <span>
                                                  {format(
                                                    new Date(ac.openStart),
                                                    "dd MMM",
                                                  )}{" "}
                                                  -{" "}
                                                  {format(
                                                    new Date(ac.openEnd),
                                                    "dd MMM",
                                                  )}
                                                </span>
                                              </Tooltip>
                                            );
                                          }
                                          return "-";
                                        })()}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        style={{ width: "160px" }}
                                      >
                                        <Tooltip title="Open">
                                          <IconButton
                                            id={`open_${rp.id}`}
                                            color="primary"
                                            onClick={(
                                              event: React.MouseEvent<HTMLButtonElement>,
                                            ) => {
                                              this.openEditRPPanel(rp.id);
                                              event.stopPropagation();
                                            }}
                                          >
                                            <OpenInNew />
                                          </IconButton>
                                        </Tooltip>
                                        {this.isAdmin && (
                                          <Tooltip title="Remove">
                                            <IconButton
                                              id={`delete_${rp.id}`}
                                              color="primary"
                                              onClick={() => {
                                                this.openDeleteDialog({
                                                  id: rp.id,
                                                  shortDescription:
                                                    rp.shortDescription,
                                                });
                                              }}
                                            >
                                              <Delete />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </Draggable>
                              );
                            },
                          )}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </TableContainer>
            </ThemeProvider>
          </Container>

          <br />
        </div>

        {/* Delete Dialog for Rp */}
        <Popup
          isPopupOpen={this.state.isDeleteRpDialogOpened}
          closeOnOutsideClick={true}
          title={`Delete ${this.state.rpToDelete.shortDescription}`}
          content={[
            "Please note:",
            "● This action is irreversible.",
            "● No changes will happen on the channel manager configuration.",
            "● This rate type will need to be deleted manually on the channel manager.",
            "Are you sure?",
          ]}
          action={() => {
            this.deleteRateplan(this.state.rpToDelete);
            // No need to call closeDeleteDialog here, it's called inside deleteRateplan implicitly via state update
          }}
          actionName="Delete Rate Plan"
          cancelAction={this.closeDeleteDialog}
          cancelName="Cancel"
        />

        {/* Delete Dialog for Rt */}
        <Popup
          isPopupOpen={this.state.isDeleteRtDialogOpened}
          closeOnOutsideClick={true}
          title={`Delete ${this.state.rtToDelete.shortDescription}`}
          content={[
            "Please note:",
            "● This action is irreversible.",
            "● No changes will happen on the channel manager configuration.",
            "● This room type will need to be deleted manually on the channel manager.",
            "Are you sure?",
          ]}
          action={() => {
            this.deleteRoomType(this.state.rtToDelete);
            // No need to call closeDeleteRtDialog here, it's called inside deleteRoomType implicitly via state update
          }}
          actionName="Delete Room Type"
          cancelAction={this.closeDeleteRtDialog}
          cancelName="Cancel"
        />
      </ThemeProvider>
    );
  }
}
const mapStateToProps = (state: {
  hotelData: { activeHotel: Hotel };
  user: User;
}) => ({
  activeHotel: state.hotelData.activeHotel,
  user: state.user,
});
const mapDispatchToProps = (
  dispatch: (action: ReturnType<typeof setActiveHotel>) => void,
) => {
  return {
    setActiveHotel: (hotel: Hotel) => dispatch(setActiveHotel(hotel)),
  };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(RoomConfig));
